import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import { Trash2 } from "lucide-react";
import Checkbox from "components/common/Checkbox";
import Radio from "components/common/Radio";
import Loading from "components/common/Loading";
import { CalendarIntegration } from "protogen/calendar_service_pb";
import {
  CLIENT_ID,
  INTEGRATIONS_REDIRECT_URI,
} from "components/calendar/integrations/constants";
import {
  useConnectGoogleIntegration,
  useGetMemberCalendarIntegrations,
  useUpdateCalendarIntegration,
} from "services/calendar";

// Generic integrations flow. Users pick a provider (Google for now), then the sub-products and
// permissions they want; we request all the matching scopes in a single OAuth consent and the
// backend records them on one integration row.

const GOOGLE_SCOPES = {
  identity: "email",
  calendarReadWrite: "https://www.googleapis.com/auth/calendar",
  calendarReadOnly: "https://www.googleapis.com/auth/calendar.readonly",
  gmailReadOnly: "https://www.googleapis.com/auth/gmail.readonly",
};

type CalendarPermission = "read_write" | "read_only";

const buildGoogleAuthUrl = (scopes: string[]) => {
  const scope = encodeURIComponent(scopes.join(" "));
  return (
    `https://accounts.google.com/o/oauth2/v2/auth?scope=${scope}` +
    `&access_type=offline&response_type=code&client_id=${CLIENT_ID}` +
    `&redirect_uri=${INTEGRATIONS_REDIRECT_URI}&prompt=consent`
  );
};

export default () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const [addIntegrationOpen, setAddIntegrationOpen] = useState(false);
  const [calendarChecked, setCalendarChecked] = useState(false);
  const [calendarPermission, setCalendarPermission] =
    useState<CalendarPermission>("read_write");
  const [gmailChecked, setGmailChecked] = useState(false);

  const {
    request: connectRequest,
    loading: connecting,
    error: connectError,
  } = useConnectGoogleIntegration();
  const [connected, setConnected] = useState(false);

  const { request: fetchIntegrations, data: integrationsData } =
    useGetMemberCalendarIntegrations();
  const { request: updateIntegration } = useUpdateCalendarIntegration();

  // Generic integrations live on google rows; calendar-only (url/file) rows belong on the calendar
  // settings page, so we only surface google here.
  const googleIntegrations = (
    integrationsData?.importIntegrations || []
  ).filter(
    (i: CalendarIntegration) =>
      i.provider === "google" && i.status !== "deactivated",
  );

  useEffect(() => {
    fetchIntegrations();
  }, []);

  // OAuth callback: Google redirects back here with ?code & the granted ?scope. Exchange happens
  // server-side via the connect RPC; we forward the granted scope so the backend records exactly
  // what the user approved.
  const code = searchParams.get("code");
  const grantedScope = searchParams.get("scope");
  useEffect(() => {
    if (!code) return;
    const connect = async () => {
      const resp = await connectRequest({
        accessCode: code,
        grantedScope: grantedScope || "",
        accessRedirectUri: INTEGRATIONS_REDIRECT_URI,
        familyRef: "",
      });
      if (resp) {
        setConnected(true);
        fetchIntegrations();
      }
      // Drop the oauth params so a refresh doesn't retry an already-spent code.
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    };
    connect();
  }, [code]);

  const onDeleteIntegration = async (ref: string) => {
    const resp = await updateIntegration({
      integrationRef: ref,
      deactivate: true,
      activate: false,
      enableCalendarId: "",
      disableCalendarId: "",
    });
    if (resp) {
      fetchIntegrations();
    }
  };

  const selectedScopes = useMemo(() => {
    const scopes = [GOOGLE_SCOPES.identity];
    if (calendarChecked) {
      scopes.push(
        calendarPermission === "read_write"
          ? GOOGLE_SCOPES.calendarReadWrite
          : GOOGLE_SCOPES.calendarReadOnly,
      );
    }
    if (gmailChecked) {
      scopes.push(GOOGLE_SCOPES.gmailReadOnly);
    }
    return scopes;
  }, [calendarChecked, calendarPermission, gmailChecked]);

  const nothingSelected = !calendarChecked && !gmailChecked;

  const beginGoogleOAuth = () => {
    window.location.href = buildGoogleAuthUrl(selectedScopes);
  };

  const sectionSx = {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #D4D4D4",
  };

  // Summarize the products connected on a google integration row (calendar access level + gmail).
  const connectedProducts = (integration: CalendarIntegration): string[] => {
    const products: string[] = [];
    if (integration.accessScope === "read_write") {
      products.push("Calendar (read & write)");
    } else if (integration.accessScope === "read_only") {
      products.push("Calendar (read only)");
    } else if (integration.accessScope === "freebusy") {
      products.push("Calendar (free/busy)");
    }
    if (integration.grantedScopes?.includes(GOOGLE_SCOPES.gmailReadOnly)) {
      products.push("Gmail (read only)");
    }
    return products;
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography variant="h2">Integrations</Typography>
        <Typography variant="bodySmall" color="text.secondary">
          Connect your accounts to Faye. Choose what you'd like to share — we'll
          ask for everything in a single step.
        </Typography>
      </Box>

      {connected && !connectError && (
        <Alert>
          <AlertTitle sx={{ marginBottom: 0 }}>Google connected</AlertTitle>
        </Alert>
      )}
      {connectError && (
        <Alert severity="error">
          <AlertTitle>Couldn't connect Google</AlertTitle>
          {connectError}
        </Alert>
      )}
      {connecting && <Loading />}

      {/* Add integration */}
      <Box display="flex" flexDirection="column" gap="12px">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="16px"
          minHeight="40px"
        >
          <Typography variant="h3">Add integration</Typography>
          {!addIntegrationOpen && (
            <Button
              variant="outlined"
              onClick={() => setAddIntegrationOpen(true)}
            >
              Add integration
            </Button>
          )}
        </Box>

        <Collapse in={addIntegrationOpen} unmountOnExit>
          <Box sx={sectionSx}>
            <Typography variant="h3">Google</Typography>

            <Box display="flex" flexDirection="column" gap="4px">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={calendarChecked}
                    onChange={(e) => setCalendarChecked(e.target.checked)}
                  />
                }
                label="Calendar"
              />
              {calendarChecked && (
                <RadioGroup
                  value={calendarPermission}
                  onChange={(e) =>
                    setCalendarPermission(e.target.value as CalendarPermission)
                  }
                  sx={{ paddingLeft: "32px" }}
                >
                  <FormControlLabel
                    value="read_write"
                    control={<Radio size="small" />}
                    label="Read and write (recommended)"
                  />
                  <FormControlLabel
                    value="read_only"
                    control={<Radio size="small" />}
                    label="Read only"
                  />
                </RadioGroup>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={gmailChecked}
                    onChange={(e) => setGmailChecked(e.target.checked)}
                  />
                }
                label="Gmail (read-only)"
              />
            </Box>

            <Button
              variant="contained"
              disabled={nothingSelected || connecting}
              onClick={beginGoogleOAuth}
              sx={{ alignSelf: "flex-start" }}
            >
              Connect Google
            </Button>

            <Typography variant="bodySmall" color="text.secondary">
              Faye's use and transfer of information received from Google APIs
              adheres to the{" "}
              <Link
                target="_new"
                href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
              >
                Google API Services User Data Policy
              </Link>
              , including the Limited Use requirements.
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {/* Connected integrations */}
      {googleIntegrations.length > 0 && (
        <Box display="flex" flexDirection="column" gap="12px">
          <Typography variant="h3">Connected</Typography>
          {googleIntegrations.map((integration: CalendarIntegration) => (
            <Box
              key={integration.ref}
              sx={{
                ...sectionSx,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <Box display="flex" flexDirection="column" gap="4px">
                <Typography variant="bodyHeavy">{integration.name}</Typography>
                <Typography variant="bodySmall" color="text.secondary">
                  {connectedProducts(integration).join(" • ") ||
                    "No products connected"}
                </Typography>
              </Box>
              <IconButton
                aria-label="delete"
                onClick={() => onDeleteIntegration(integration.ref)}
              >
                <Trash2 width="20" color="#8E9598" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
